/**
 * Supabase persistence helpers for floorplan drawings and overlay images.
 *
 * Floorplan drawings (vector elements + settings) → public.floorplans table.
 * Overlay images (background photo/PNG)           → floorplan-overlays Storage bucket.
 */

import { supabase } from './supabase';
import type { DBFloorplan } from './supabase';
import type { FloorplanData } from '../types';

const STORAGE_BUCKET = 'floorplan-overlays';
const LEGACY_STORAGE_KEY = 'netprobuilder-floorplans';

// ─── Conversion helpers ───────────────────────────────────────────────────────

export function dbRowToFloorplan(row: DBFloorplan, overlayUrl?: string): FloorplanData & { overlayPath?: string | null; overlayUrl?: string } {
  return {
    id: row.legacy_id ?? row.id,
    _dbId: row.id,
    name: row.name,
    settings: row.settings as unknown as FloorplanData['settings'],
    elements: (row.elements ?? []) as FloorplanData['elements'],
    width: row.width,
    height: row.height,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    overlayPath: row.overlay_path,
    overlayUrl: overlayUrl ?? undefined,
  } as FloorplanData & { _dbId: string; overlayPath?: string | null; overlayUrl?: string };
}

// ─── Table CRUD ───────────────────────────────────────────────────────────────

export async function fetchFloorplansForCollection(collectionId: string): Promise<DBFloorplan[]> {
  const { data, error } = await supabase
    .from('floorplans')
    .select('*')
    .eq('collection_id', collectionId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as DBFloorplan[];
}

export async function upsertFloorplanRecord(
  collectionId: string,
  floorplan: FloorplanData & { _dbId?: string; overlayPath?: string | null },
  sortOrder = 0
): Promise<DBFloorplan> {
  const now = new Date().toISOString();

  const row: Partial<DBFloorplan> & { collection_id: string } = {
    collection_id: collectionId,
    name: floorplan.name,
    settings: floorplan.settings as unknown as Record<string, unknown>,
    elements: floorplan.elements as unknown,
    width: floorplan.width,
    height: floorplan.height,
    overlay_path: floorplan.overlayPath ?? null,
    sort_order: sortOrder,
    updated_at: now,
  };

  // If we know the DB uuid (loaded from Supabase), update in-place.
  const dbId = (floorplan as any)._dbId as string | undefined;
  if (dbId) {
    const { data, error } = await supabase
      .from('floorplans')
      .update(row)
      .eq('id', dbId)
      .select()
      .single();

    if (error) throw error;
    return data as DBFloorplan;
  }

  // Otherwise insert (may have a legacy_id from localStorage).
  const insertRow = {
    ...row,
    legacy_id: floorplan.id !== undefined ? String(floorplan.id) : null,
    created_at: floorplan.createdAt ? floorplan.createdAt.toISOString() : now,
  };

  const { data, error } = await supabase
    .from('floorplans')
    .insert(insertRow)
    .select()
    .single();

  if (error) throw error;
  return data as DBFloorplan;
}

export async function deleteFloorplanRecord(dbId: string): Promise<void> {
  const { error } = await supabase.from('floorplans').delete().eq('id', dbId);
  if (error) throw error;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

/** Build the public URL for a given storage path. */
export function overlayPublicUrl(path: string): string {
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Upload a file or Blob as the overlay for a floorplan.
 * Returns the storage path (not the full URL).
 */
export async function uploadOverlay(
  collectionId: string,
  fileOrBlob: File | Blob,
  filename: string
): Promise<string> {
  const ext = filename.split('.').pop() ?? 'png';
  const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  const path = `${collectionId}/${uuid}.${ext}`;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, fileOrBlob, {
      upsert: false,
      contentType: fileOrBlob instanceof File ? fileOrBlob.type : 'image/png',
    });

  if (error) throw error;
  return path;
}

/**
 * Upload a base64 data URL (e.g. from canvas.toDataURL) as an overlay.
 * Returns the storage path.
 */
export async function uploadOverlayFromDataUrl(
  collectionId: string,
  dataUrl: string
): Promise<string> {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const ext = mime.split('/')[1] ?? 'png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  return uploadOverlay(collectionId, blob, `overlay.${ext}`);
}

/** Delete an overlay from Storage by its storage path. */
export async function deleteOverlay(path: string): Promise<void> {
  const { error } = await supabase.storage.from(STORAGE_BUCKET).remove([path]);
  if (error) console.warn('[deleteOverlay]', error.message);
}

// ─── LocalStorage migration ───────────────────────────────────────────────────

/**
 * One-time migration: read floorplans from localStorage and insert them into
 * Supabase. Skips any that already have a matching legacy_id for this collection.
 */
export async function migrateLocalFloorplans(collectionId: string): Promise<void> {
  let local: any[] = [];
  try {
    local = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) ?? '[]');
  } catch {
    return;
  }

  if (local.length === 0) return;

  // Fetch existing rows so we can skip already-migrated ones.
  const existing = await fetchFloorplansForCollection(collectionId);
  const existingLegacyIds = new Set(existing.map((r) => r.legacy_id).filter(Boolean));

  for (const fp of local) {
    if (!fp.id || existingLegacyIds.has(String(fp.id))) continue;

    // Overlay images stored as base64 are skipped (too large for migration upload).
    // They will be re-imported by the user on demand.
    const floorplanData: FloorplanData & { overlayPath: null } = {
      id: fp.id,
      name: fp.name ?? 'Untitled Floorplan',
      settings: fp.settings ?? {},
      elements: fp.elements ?? [],
      width: fp.width ?? 1920,
      height: fp.height ?? 1080,
      createdAt: fp.createdAt ? new Date(fp.createdAt) : new Date(),
      updatedAt: fp.updatedAt ? new Date(fp.updatedAt) : new Date(),
      overlayPath: null,
    };

    try {
      await upsertFloorplanRecord(collectionId, floorplanData);
    } catch (err) {
      console.warn('[migrateLocalFloorplans] skipping', fp.id, err);
    }
  }
}
